/**
 * Turning a chunk of blocks into geometry.
 *
 * The naive approach — one cube per solid block — would emit 32,768 boxes per
 * chunk, nearly all of them buried where nobody can see them. A chunk of solid
 * ground would be 393,216 triangles to draw six visible faces.
 *
 * **Face culling** emits a face only where a solid block touches air. For
 * terrain that is essentially just the surface skin, which is a reduction of
 * one to two orders of magnitude for about thirty lines of code.
 *
 * Greedy meshing — merging coplanar faces of the same material into larger
 * quads — would cut it further. It is deliberately *not* done yet: culling is
 * simple enough to be obviously correct, and the next optimisation should be
 * chosen from a profile rather than a guess (newSession §7 rule 7).
 *
 * **Ambient occlusion (T84).** Each vertex is darkened by how enclosed its
 * corner is — the three blocks that meet there, tested against the empty cell
 * the face looks into. It costs one number per vertex and no extra draw calls,
 * and it is the single largest step toward brief §2 pillar 5, *"beautiful, not
 * blocky-ugly"*: without it a voxel world is flat colour meeting flat colour
 * and every edge disappears, which is exactly how the terrain read before.
 *
 * ⚠️ **It is also why greedy meshing is now a harder change than it was.**
 * Merged quads would have to agree on the occlusion at every corner they
 * span, so the two optimisations interact. Worth knowing before anyone
 * profiles their way into it.
 */

import {
  BLOCK_AIR,
  CHUNK_SIZE,
  MAX_LIGHT,
  blockHeight,
  findEmitters,
  floodFrom,
  isDriftwood,
  isBur,
  isBurPart,
  isRum,
  isRumPart,
  isLoom,
  isLoomPart,
  isKljasteinn,
  isPitBlock,
  isPitFloor,
  isTorch,
  PEG_BLOCK,
  isGate,
  blockIdForKey,
  packCell,
  passesLight,
  type Emitter,
  isCutout,
  isSolid,
  blockIsCross,
  blockIsWaterlogged,
  isFluid,
  tileLayer,
  edgeLayer,
  type FaceKind,
  BLOCK,
  baseBlockOf,
  stateOf,
} from '@lofi/shared';
import { jataBoxes, type JataFacing, type JataStuff } from './jatamesh.ts';

/**
 * Does the neighbour hide this face completely?
 *
 * ⚠️ **This replaced "is the neighbour air", and the old rule was only correct
 * while every block was a full cube.** `kjarr` (T95) is the first that is not —
 * a 25 cm mat of birch scrub — and our own research predicted exactly this:
 * *"a slab does not fully occlude the face below it, so its neighbours must be
 * drawn"* (`Development/Minecraft/02_Building_Mechanics.md` §5.4). Under the old
 * rule a short block punched holes in terrace risers and tree trunks beside it.
 *
 * Three cases, and with every height at 1 this reduces exactly to the old rule:
 *
 * | Face | Hidden when |
 * |---|---|
 * | **top** | this block is full height — otherwise its top lies *inside* its own cell, where nothing can reach it |
 * | **bottom** | the neighbour below is full height, so its top meets the shared plane |
 * | **side** | the neighbour is at least as tall as this block |
 *
 * ⚠️ A short block still has a **full footprint**, which is why the top face of
 * the block underneath it is still hidden. Height is the only thing that shrank.
 *
 * ## 🍃 And the second loosening — a face you can see THROUGH (T144)
 *
 * 🔴 **Nothing hides a face when either side is a cutout block.** Culling
 * assumes that a covered face is an invisible face, and a leaf block breaks that
 * assumption in both directions: you can see out of the crown through the holes,
 * and you can see into it.
 *
 * 🎯 **This is the whole of why haugur's birches were *"still blocks and funny
 * looking."*** Every face inside a crown was culled, so nine leaf blocks were
 * drawn as one closed box with a flat green lid — **and no texture could have
 * fixed it, because the faces that would have carried it did not exist.**
 *
 * ⚠️ **BOTH sides, not just the neighbour.** The trunk's face against a leaf has
 * to be drawn too, or you look through a hole in the crown at a hole in the
 * tree.
 */
/**
 * 🔴 **Blocks this mesher does not draw — and therefore blocks that hide
 * NOTHING.**
 *
 * ⚠️ **ONE list, asked by BOTH halves, and that is the whole point of it
 * existing.** Skipping a block's own faces and leaving it as an occluder is
 * **half a change**, and the missing half is invisible until you stand next to
 * one. 🔴 **It has now happened twice**: the torch on 2026-08-31, and driftwood
 * on 2026-09-02 — 🎯 haugur, both times, within minutes of first seeing it:
 * *"the block under it becomes almost transparent."*
 *
 * ⚠️ **The second time the skip was added and this was not**, on a note claiming
 * that `solid: false` already covered it. **It does not**: culling reads
 * `blockHeight`, and a height of anything above zero answers "the cell is not
 * empty" however little of it is really filled. **Two places had to agree and
 * only one was changed.**
 *
 * 👉 **Anything drawn beside the terrain rather than in it belongs here** —
 * T159's crosses are next in line.
 */
/**
 * ⚡ **Remembered per block id, because this was the single most expensive
 * question the mesher asks.**
 *
 * 🔴 **Each of the four checks below does a STRING-KEYED map lookup on every
 * call** — `blockIdForKey(TORCH_KEY)` and friends — **for constants that never
 * change**; two of them do a second lookup on top. 📏 **`hidesFace` calls this
 * TWICE for every face it considers**, so one chunk of solid rock asked it
 * nearly 400,000 times.
 *
 * ⚠️ **0 = not asked yet, 1 = yes, 2 = no.** A lazy fill rather than a table
 * built at module load, because these predicates live in four other modules and
 * an import cycle at startup is a worse problem than a first-call cost.
 *
 * 🔴 **The palette never changes while the game runs** — it is generated at
 * build time and pinned by `palette-lock.json` — so an answer cached here can
 * never go stale.
 */
const OUTSIDE = new Uint8Array(4096);
const PEG_ID = blockIdForKey(PEG_BLOCK) ?? -1;

function drawnOutsideTheMesh(id: number): boolean {
  if (id < 0 || id >= OUTSIDE.length) return false;
  // eslint-disable-next-line security/detect-object-injection -- bounds-checked above, into an array this module owns
  const known = OUTSIDE[id];
  if (known !== 0) return known === 1;
  /* 🏠 The storehouse's cells are solid and never cubes — burmesh.ts draws one
     model over the lot. D109. */
  /* 🪵 D128 — the tether peg is a post drawn by torchmesh.ts, never a cube. */
  /* 🚪 D130 — the pen gate is posts and rails drawn by gatemesh.ts. */
  const answer =
    isTorch(id) ||
    isDriftwood(id) ||
    isBur(id) ||
    isBurPart(id) ||
    id === PEG_ID ||
    isGate(id) ||
    /* 🛏️ D133 — the rúm is a model drawn by rummesh.ts, both cells. */
    isRum(id) ||
    isRumPart(id) ||
    /* 🧵 D136 — the loom is his model A and our own warp and cloth
       (loommesh.ts), all four cells; a kljásteinn is his stone B. */
    isLoom(id) ||
    isLoomPart(id) ||
    isKljasteinn(id) ||
    /* 🏠 D135 — the pit house: walls and ceiling drawn by pithousemesh.ts on
       the block faces, the step, oven, bench and door as models. Its earth
       FLOOR is an ordinary cube, and this mesh draws it. */
    (isPitBlock(id) && !isPitFloor(id));
  // eslint-disable-next-line security/detect-object-injection -- bounds-checked above, into an array this module owns
  OUTSIDE[id] = answer ? 1 : 2;
  return answer;
}

/**
 * 🥣 **Is this a jata** — any facing, any fill (T286)? Its boxes are drawn by
 * {@link meshChunk} itself, in this mesh and this material, and it hides
 * nothing: it covers only part of its cell, like the torch.
 */
const JATA_ID = BLOCK['jata'] ?? -1;
const isJata = (id: number): boolean => id !== BLOCK_AIR && baseBlockOf(id) === JATA_ID;

/** 🥣 Which tile each part of a jata wears — textures the game already has. */
const JATA_TILE: ReadonlyMap<JataStuff, readonly [string, FaceKind]> = new Map([
  ['stone', ['grjotgardur', 'side']],
  ['wood', ['rekaplanki', 'side']],
  ['hay', ['slaegja', 'top']],
]);

function hidesFace(block: number, neighbour: number, dy: number, height: number): boolean {
  /* 🥣 T286 — a jata is part of a cell; nothing hides against it, and its own
     boxes are never culled (it draws them whole). */
  if (isJata(block) || isJata(neighbour)) return false;
  /**
   * 🌊 **THE SEA STAYS A SKIN AROUND A PLANT GROWING IN IT — T242, D120.**
   *
   * 🔴 **This must come FIRST, before the cutout line below, and that ordering
   * IS the fix.** Söl and beltisþari are cutout crosses standing in cells that
   * used to be `vatn`. Without this, the very next line answers `false` for
   * every one of them, **the sea draws a face against each plant, and every
   * söl and every kelp sits in a glass box.**
   *
   * ⚠️ **The water's own rule twenty lines down already says why that is
   * wrong** — *"fluid against fluid still hides, and must. A body of water is
   * meant to be a SKIN, not a stack of glass boxes."* **A waterlogged cell is
   * part of that body.** So the sea hides against it exactly as it hides
   * against itself, and the plant is still drawn, because a cross is drawn on
   * its own path and never through this function.
   *
   * 👉 **It was found by reading this function rather than by shipping it**,
   * which is worth saying because the last two faults of this exact family —
   * the torch hiding what it did not cover, and water covering what it no
   * longer stopped the light through — were both found by haugur looking at the
   * screen. See {@link blockIsWaterlogged}.
   */
  if (isFluid(block) && blockIsWaterlogged(neighbour)) return true;
  if (isCutout(block) || isCutout(neighbour)) return false;
  /**
   * 🔴 **A torch hides NOTHING, and this was a real hole in the world.**
   *
   * 🎯 **haugur, 2026-08-31, the first time he placed one:** *"if placed on
   * wall the ground infront falls away, if placed on ground the top of block
   * falls away."* **He was looking through the terrain.**
   *
   * ⚠️ **Culling asks about the CELL, and the torch answered for the cell
   * while occupying almost none of it.** `blockHeight` is 0.625 for a torch, so
   * the `nh === 0` air check let it through, and the top face of whatever it
   * stood on was then culled as *covered* — by a post 0.125 m square.
   *
   * 👉 **The general rule, and T159's crosses will need it next:** a block the
   * MESHER DOES NOT DRAW cannot hide anything, because there is no surface
   * there to hide behind. **Skipping a block's own faces and leaving it as an
   * occluder is half a change**, and the missing half is invisible until you
   * stand next to one.
   */
  if (drawnOutsideTheMesh(block) || drawnOutsideTheMesh(neighbour)) return false;
  /**
   * 🌊 **WATER HIDES NOTHING BEHIND IT — T158, and this is the whole fault.**
   *
   * 🔴 **Until water became see-through, the seabed was NOT DRAWN.** A full
   * cube of water covered the top of the block under it, so this function
   * culled that face as *covered* — correctly, while water was opaque. The
   * moment you can see through it, the sea floor is **a hole into nothing**,
   * and so is every river bed and every bank below the waterline.
   *
   * ⚠️ **It is the torch bug above, wearing the other coat.** That one was *a
   * block that is barely there hiding a face it does not cover*; this is *a
   * block that covers the face completely but no longer stops the light.*
   * 👉 **The general rule both arrive at: a face is hidden only by something
   * you cannot see past.**
   *
   * 🔴 **Fluid against fluid still hides**, and must. A body of water is meant
   * to be a SKIN — its surface and its edges — not a stack of glass boxes.
   * Drawing the inner faces would both cost a chunk of sea 32 times its faces
   * and make deep water read as darker in bands, because each pane blends
   * again.
   *
   * ⚠️ **Measured cost:** the top face of the ground under water, plus the
   * sides where a bank drops below the line. In a chunk of open sea that is
   * about one extra face per column against a surface of the same size — call
   * it double for a seabed chunk, and nothing at all inland.
   */
  if (isFluid(neighbour) && !isFluid(block)) return false;
  const nh = blockHeight(neighbour);
  if (nh === 0) return false;
  if (dy > 0) return height === 1;
  if (dy < 0) return nh === 1;
  return nh >= height;
}

/**
 * 🌊 **One drawable bucket of a chunk** — the ground, or the water (T158).
 *
 * ⚠️ **This used to be the whole of `ChunkMesh`**, and it was split rather than
 * copied when water gained a material of its own, so the two buckets cannot
 * drift apart in what they carry.
 */
export interface ChunkGeometry {
  /** Triples of x,y,z, chunk-local. */
  readonly positions: Float32Array;
  readonly normals: Float32Array;
  /**
   * One per vertex: how much light this corner keeps, from ambient occlusion.
   *
   * ⚠️ Colour moved into the texture (T84), and in T94 this stopped being a
   * grey `color` attribute and split in two. **The two halves have to reach
   * the shader separately because they fade out at different distances** —
   * see `jitter` below and `main.ts`.
   */
  readonly ao: Float32Array;
  /**
   * One per vertex: the block's own brightness offset, 0.92…1.08.
   *
   * Constant across all 24 vertices of a block, by construction.
   */
  readonly jitter: Float32Array;
  /**
   * 🔦 One per vertex: how much **added** light this corner receives, 0–1.
   *
   * 🔴 **0 means "nothing was added here", NOT "this is dark."** The sun and
   * the sky light this world and always have; this is only what torches and
   * fires put on top. ⚠️ **So the shader must ADD it and never multiply by
   * it** — multiplying would black out every surface in Iceland the moment the
   * attribute existed.
   */
  readonly light: Float32Array;
  /** Pairs of u,v into the block's tile. */
  readonly uvs: Float32Array;
  /** One per vertex: which layer of the texture array this face samples. */
  readonly layers: Float32Array;
  readonly indices: Uint32Array;
  readonly faceCount: number;
}

/**
 * Everything one chunk needs drawn, plus the light it can be asked about.
 *
 * ⚠️ **The ground's own geometry is spread at the top level rather than nested
 * under a `solid` key**, so every existing caller — and every existing test —
 * goes on reading `mesh.positions` and `mesh.faceCount` exactly as before.
 */
export interface ChunkMesh extends ChunkGeometry {
  /**
   * 🌊 **The water's faces, for the material that blends** (T158).
   *
   * ⚠️ **`faceCount === 0` is the ordinary case** — most of Iceland is dry, and
   * a caller must skip an empty bucket rather than build an empty mesh for
   * every chunk in the country.
   */
  readonly water: ChunkGeometry;
  /**
   * 🔦 **How much added light one CELL receives, 0–1** — chunk-local
   * coordinates, and it may be asked about a cell outside this chunk.
   *
   * 🔴 **The same flood the `light` attribute above is built from, handed out
   * rather than thrown away.** ⚠️ **Anything drawn OUTSIDE the world mesh has no
   * other way to know a torch is there** — the flood lives for the length of one
   * mesh and the per-vertex attribute is all that used to survive it.
   *
   * 🎯 **haugur, 2026-09-04:** *"i placed some kyndlar on the 3 houses, it worked
   * fine but the houses don light up in the dark from the kyndlar."* **The turf
   * house is a 3D model drawn beside the terrain, so it never saw the
   * attribute.** ✅ This is what lets it ask.
   *
   * 🔴 **0 means "nothing was added here", NOT "this is dark"** — the same
   * warning the attribute carries, and for the same reason.
   */
  readonly litAt: (x: number, y: number, z: number) => number;
}

/** The six neighbours, and the quad wound anticlockwise when seen from outside. */
const FACES = [
  {
    dir: [1, 0, 0],
    corners: [
      [1, 0, 0],
      [1, 1, 0],
      [1, 1, 1],
      [1, 0, 1],
    ],
  },
  {
    dir: [-1, 0, 0],
    corners: [
      [0, 0, 1],
      [0, 1, 1],
      [0, 1, 0],
      [0, 0, 0],
    ],
  },
  {
    dir: [0, 1, 0],
    corners: [
      [0, 1, 1],
      [1, 1, 1],
      [1, 1, 0],
      [0, 1, 0],
    ],
  },
  {
    dir: [0, -1, 0],
    corners: [
      [0, 0, 0],
      [1, 0, 0],
      [1, 0, 1],
      [0, 0, 1],
    ],
  },
  {
    dir: [0, 0, 1],
    corners: [
      [1, 0, 1],
      [1, 1, 1],
      [0, 1, 1],
      [0, 0, 1],
    ],
  },
  {
    dir: [0, 0, -1],
    corners: [
      [0, 0, 0],
      [0, 1, 0],
      [1, 1, 0],
      [1, 0, 0],
    ],
  },
] as const;

/** Read one component of a vector without indexing it by a variable. */
const axisValue = (v: readonly number[], axis: number): number =>
  axis === 0 ? (v[0] ?? 0) : axis === 1 ? (v[1] ?? 0) : (v[2] ?? 0);

/** A unit step along one axis, in the given direction. */
const axisOffset = (axis: number, sign: number): readonly [number, number, number] =>
  axis === 0 ? [sign, 0, 0] : axis === 1 ? [0, sign, 0] : [0, 0, sign];

interface FaceCorner {
  readonly offset: readonly [number, number, number];
  /**
   * Where this corner sits in the tile.
   *
   * ⚠️ **On a side face, `v = 0` is the TOP of the block**, because row 0 of a
   * tile is the fringe — the moss spilling over the edge. Get this the wrong
   * way up and the moss appears along the bottom of the wall, buried in the
   * ground, which is both wrong and baffling to look at.
   */
  readonly uv: readonly [number, number];
  /**
   * Where the three blocks that can shadow this corner sit, **relative to the
   * air cell in front of the face** — the two edge-on neighbours and the one
   * diagonally across from them.
   */
  readonly side1: readonly [number, number, number];
  readonly side2: readonly [number, number, number];
  readonly diagonal: readonly [number, number, number];
}

/**
 * `FACES`, with everything ambient occlusion needs worked out once at load.
 *
 * A face's two in-plane axes are simply the two its normal ignores. For each
 * corner, the sign along each of those axes says which way the occluders lie.
 * Precomputed because it is fixed geometry, and because doing it in the inner
 * loop would mean indexing vectors by a variable 12 times per face.
 */
const FACE_GEOMETRY = FACES.map((face) => {
  const axes: number[] = [];
  if (face.dir[0] === 0) axes.push(0);
  if (face.dir[1] === 0) axes.push(1);
  if (face.dir[2] === 0) axes.push(2);
  const a1 = axes[0] ?? 0;
  const a2 = axes[1] ?? 1;

  const corners: FaceCorner[] = face.corners.map((c) => {
    const side1 = axisOffset(a1, axisValue(c, a1) === 1 ? 1 : -1);
    const side2 = axisOffset(a2, axisValue(c, a2) === 1 ? 1 : -1);

    /* Horizontal faces lie flat, so both tile axes are ground axes. Vertical
       faces take their horizontal axis from whichever of x/z the face does not
       face, and their vertical one from height — flipped, so the top of the
       block is the top of the tile. */
    const uv: readonly [number, number] =
      face.dir[1] === 0
        ? [axisValue(c, a1 === 1 ? a2 : a1), 1 - (c[1] ?? 0)]
        : [c[0] ?? 0, c[2] ?? 0];

    return {
      offset: [c[0], c[1], c[2]] as const,
      uv,
      side1,
      side2,
      diagonal: [side1[0] + side2[0], side1[1] + side2[1], side1[2] + side2[2]] as const,
    };
  });

  return { dir: [face.dir[0], face.dir[1], face.dir[2]] as const, corners };
});

/**
 * How much light a vertex keeps, by how enclosed its corner is.
 *
 * ⚠️ **Applied to linear colour, which is why it is physically meaningful.**
 * Occlusion is a fraction of the incoming light that never arrives, and since
 * the vertex buffer now carries linear light rather than sRGB values,
 * multiplying by a fraction does what the word means. Before the colour-space
 * fix the same multiply would have been darkening a gamma-encoded number, which
 * is not a quantity of anything.
 *
 * Written as a ternary chain rather than an array lookup so nothing indexes by
 * a variable — same reason as `axisValue`.
 */
const aoShade = (open: number): number =>
  open <= 0 ? 0.6 : open === 1 ? 0.76 : open === 2 ? 0.89 : 1;

/**
 * How far a single block's brightness may drift from its palette colour (T84).
 *
 * 35% of the world is `birki`, and a whole hillside of it was one identical
 * value — but a 16×16 texture is **sub-pixel beyond about 100 m**, so no
 * texture can break that up at the range where most of the terrain is actually
 * seen. A per-block value change survives, because it is carried by the
 * geometry rather than by a tile.
 *
 * ⚠️ **T84 called this "the one thing that works at every distance". It was
 * exactly backwards, and it is half of why distant ground crawled (T94).**
 * Detail carried by geometry is detail *nothing filters*: mipmaps only ever
 * touch the texture, so once a block is smaller than a pixel this becomes a
 * random number drawn afresh every time the camera moves. That is the
 * definition of aliasing. It works up close and has to be faded out with
 * distance — which the shader now does.
 *
 * ±8%, from `Design/00_Visual_Language.md` §12.7.
 */
const VALUE_JITTER = 0.08;

/**
 * A stable pseudo-random 0…1 from a world position.
 *
 * ⚠️ **World position, never chunk-local** — that is the whole requirement. Keyed
 * on chunk-local coordinates the same pattern would repeat in every chunk, and a
 * 32 m grid across the landscape is far more obvious than the flatness it was
 * meant to cure.
 *
 * `Math.imul` because plain `*` on these constants leaves float precision and
 * stops being a hash. No allocation, no state, and the same block is the same
 * shade on every client and after every reload — which matters, because a
 * player who breaks a block and puts it back must not see it change colour.
 */
function hashPosition(x: number, y: number, z: number): number {
  let h = Math.imul(x, 374761393) ^ Math.imul(y, 668265263) ^ Math.imul(z, 1274126177);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/**
 * The brightness multiplier for one block.
 *
 * Symmetric about 1, so the **average is exactly the palette colour** — which is
 * the rule that keeps §12.4's value relationships intact at the distance where
 * value is the only lever left.
 */
function jitterAt(x: number, y: number, z: number): number {
  return 1 + (hashPosition(x, y, z) - 0.5) * 2 * VALUE_JITTER;
}

/**
 * 🌸 **How far a cross may shift sideways from the middle of its cell**, in
 * metres. Minecraft allows a quarter of a block; this is a little less, because
 * our scrub is knee-high and a big shift makes a bush look like it belongs to
 * the cell next door.
 */
const CROSS_OFFSET = 0.18;

/**
 * 🌸 **How wide one plane is, as a multiple of how TALL the plant is.**
 *
 * 🔴 **This was a fixed 0.5 — the cell's own diagonal — and it was wrong.**
 * Minecraft's cross is as wide as it is tall; ours is 0.25 m tall, so a plane
 * spanning the cell was **nearly three times wider than it was high** and a
 * patch of it read as smears lying on the ground rather than as plants. **The
 * geometry was right and the proportion was not**, and only a baked picture
 * said so.
 *
 * ⚠️ **Capped at the cell diagonal**, so a metre-tall plant is Minecraft's
 * shape exactly and nothing ever spills further than a neighbouring cell.
 */
const CROSS_WIDTH_PER_HEIGHT = 1.4;
const CROSS_MAX_HALF_WIDTH = 0.5;

/** The two diagonals, as (x,z) → (x,z) unit steps from the centre. */
const CROSS_PLANES: readonly (readonly [number, number, number, number])[] = [
  [-1, -1, 1, 1],
  [-1, 1, 1, -1],
];

const index = (x: number, y: number, z: number): number => x + CHUNK_SIZE * (z + CHUNK_SIZE * y);

/**
 * ⚠️ **The block's colour is no longer in this file.** It moved into the tile.
 *
 * Until T84 the mesher wrote the palette colour into the vertex buffer, and it
 * had to convert sRGB → linear on the way — three does not colour-manage a raw
 * vertex-colour attribute, so feeding it an sRGB value made every block about
 * twice as bright as authored and clipped most of the palette to white
 * (`mosahraun #77805F` reached the screen as `#FFFFEC`).
 *
 * That conversion now happens in `renderTile`, once per tile, and the vertex
 * buffer carries only **how much light this corner keeps** — ambient occlusion
 * times the value jitter, as a grey the shader multiplies the tile by.
 *
 * The split is what makes a fringe possible at all: moss spilling over the top
 * of the lava beneath it is a colour change *inside* one face, and no
 * per-vertex value can express that.
 */
const layerCache = new Map<string, number>();

/** 🌾 Cached {@link edgeLayer} — `null` remembered as "has no edge". */
const edgeCache = new Map<number, number | null>();
function edgeLayerFor(block: number): number | undefined {
  let layer = edgeCache.get(block);
  if (layer === undefined) {
    layer = edgeLayer(block) ?? null;
    edgeCache.set(block, layer);
  }
  return layer ?? undefined;
}

/**
 * 🌾 **Does the block above hide the top of this one's side?** — #813.
 * A full, solid, opaque block does; air, a plant, a torch or a short block
 * like stubble does not, so the grass under them still shows its band.
 */
/**
 * 🌿 **Is this side a grassy SLOPE rather than a bank?** — #817.
 *
 * 🎯 **haugur:** *"how can a grass block stand on another grass block with dirt
 * inbetween."* 🔴 **In nature a one-metre step in grassland is a green slope**;
 * bare earth under a grass lip is a *rofabarð*, an eroded bank. ✅ **Minecraft's
 * "Better Grass" answer**: draw the side with the TOP when the ground a step
 * down beside it is grass too.
 *
 * Only for a block that has a grass band (`edgeLayer`), only when the cell
 * beside it is OPEN (air, a plant — never stubble, whose cut edge keeps its
 * standing-grass band) and only when the block one down beside it is grass.
 * A bigger drop, or a drop onto rock, sand or water, stays a bank.
 */
function grassSlope(block: number, beside: number, besideBelow: number): boolean {
  if (edgeLayerFor(block) === undefined) return false;
  if (isSolid(beside) || isFluid(beside)) return false;
  return edgeLayerFor(besideBelow) !== undefined;
}

function coveredBy(above: number): boolean {
  return isSolid(above) && blockHeight(above) >= 1 && !isCutout(above);
}

function layerFor(block: number, dy: number): number {
  const face: FaceKind = dy === 1 ? 'top' : dy === -1 ? 'bottom' : 'side';
  const key = `${block}:${face}`;
  const cached = layerCache.get(key);
  if (cached !== undefined) return cached;
  const layer = tileLayer(block, face);
  layerCache.set(key, layer);
  return layer;
}

/**
 * Build a mesh for one chunk.
 *
 * `neighbourAt` supplies blocks in adjacent chunks. Without it, every chunk
 * would be meshed as though surrounded by air, producing a visible wall of
 * faces on all six sides of every chunk — the classic voxel chunk-seam.
 * It returns air by default, which is correct for chunks that have not arrived
 * yet: the seam appears briefly and then resolves once the neighbour loads.
 */
/**
 * 🔦 **How far outside a chunk a light can still reach into it.**
 *
 * {@link MAX_LIGHT} is the ceiling, so nothing can light a cell more than that
 * many blocks away. ⚠️ **The reach follows the CEILING and not the brightest
 * thing that exists today** — a hearth at 12 already outreaches a torch's 8, and
 * the next bright thing must not send anybody back here.
 *
 * ✅ **It is less than a chunk**, which is what keeps the search for nearby fires
 * to the 26 chunks touching this one.
 */
export const LIGHT_REACH = MAX_LIGHT;

/**
 * 🌊 **One bucket of geometry** — the ground's, or the water's (T158).
 *
 * Plain arrays while it fills, packed into typed arrays once at the end. The
 * fields are exactly the attributes {@link ChunkMesh} carries, so the face loop
 * can bind either bucket to the same names and stay one piece of code.
 */
interface Buf {
  readonly positions: number[];
  readonly normals: number[];
  readonly ao: number[];
  readonly jitter: number[];
  readonly light: number[];
  readonly uvs: number[];
  readonly layers: number[];
  readonly indices: number[];
  /** ⚠️ Mutable on purpose — the face loop counts into whichever bucket it is writing. */
  faces: number;
}

const newBuf = (): Buf => ({
  positions: [],
  normals: [],
  ao: [],
  jitter: [],
  light: [],
  uvs: [],
  layers: [],
  indices: [],
  faces: 0,
});

export function meshChunk(
  blocks: Uint16Array,
  neighbourAt: (x: number, y: number, z: number) => number = () => BLOCK_AIR,
  /**
   * Where this chunk sits in the world, so the value jitter is keyed on a
   * position that does not repeat. Defaults to the origin, which is only ever
   * right for a test.
   */
  origin: { readonly x: number; readonly y: number; readonly z: number } = { x: 0, y: 0, z: 0 },
  /**
   * 🔦 **Fires in the chunks around this one, in CHUNK-LOCAL coordinates** —
   * so a torch one block west of this chunk arrives as `x: -1`.
   *
   * 🔴 **This is a performance fix and haugur found what it replaced:**
   * *"framrate on dev has dropped to 8, framrate on staging is fine."* The first
   * version went looking for fires itself, by filling a **62³ array — 238,328
   * cells** — through `neighbourAt` on **every chunk rebuild**, whether anything
   * was burning or not. Dev was streaming 73 chunks at the time.
   *
   * ✅ **Finding a chunk's fires is one pass over an array somebody already
   * holds.** The caller does it once when a chunk arrives and caches the answer;
   * this asks for the result rather than going and looking again.
   *
   * ⚠️ **Empty is correct for a test and for an isolated chunk**, and simply
   * means no light arrives from outside.
   */
  nearby: readonly Emitter[] = [],
): ChunkMesh {
  /**
   * 🌊 **Two buckets, not one — T158.**
   *
   * Water is drawn by a **second material that blends**, so its faces cannot
   * ride in the same geometry as the ground: one draw call has one material.
   * ✅ **This is the pattern the flame, the torch post, the driftwood and the
   * smoke already follow** — *"kept beside the terrain meshes rather than
   * inside them"* — and for the same reason each of them gives.
   *
   * ⚠️ **Both buckets are filled by ONE pass over the chunk.** Meshing twice
   * would double the hot loop for a world that is mostly dry, and T218 is
   * already about this loop costing too much.
   */
  const solid = newBuf();
  const fluid = newBuf();

  const blockAt = (x: number, y: number, z: number): number => {
    if (x < 0 || x >= CHUNK_SIZE || y < 0 || y >= CHUNK_SIZE || z < 0 || z >= CHUNK_SIZE) {
      return neighbourAt(x, y, z);
    }
    return blocks[index(x, y, z)] ?? BLOCK_AIR;
  };

  /**
   * 🔦 How bright the cell at these chunk-local coordinates is, 0–1.
   *
   * ⚠️ **0 when nothing is burning in reach**, which is the ordinary case and
   * costs one pass over the chunk and no allocation at all — and 0 means *no
   * light was added here*, not *this place is dark*. The sun is somebody else's
   * business.
   */
  const emitters = [...findEmitters(blocks, CHUNK_SIZE), ...nearby];
  const lit =
    emitters.length === 0 ? null : floodFrom(emitters, (x, y, z) => passesLight(blockAt(x, y, z)));
  const litAt = (x: number, y: number, z: number): number =>
    lit === null ? 0 : (lit.get(packCell(x, y, z)) ?? 0) / MAX_LIGHT;

  for (let y = 0; y < CHUNK_SIZE; y++) {
    for (let z = 0; z < CHUNK_SIZE; z++) {
      for (let x = 0; x < CHUNK_SIZE; x++) {
        const block = blocks[index(x, y, z)] ?? BLOCK_AIR;
        if (block === BLOCK_AIR) continue;

        /**
         * 🔦 **A torch is not in this mesh, and cannot be.**
         *
         * 🔴 This loop emits **axis-aligned faces of a box scaled in Y**, and
         * that is the whole vocabulary it has. A torch is a post 0.125 m square
         * — thin in X and Z, which nothing here can express — so a torch left
         * in the world mesh is **a solid metre of orange**.
         *
         * ✅ It is drawn separately by `torch.ts` beside the terrain, for
         * exactly the reasons `flame.ts` already is: not a cube, not opaque,
         * not shaded and not merge-able. **Every one of those is a reason it
         * cannot ride in here.**
         *
         * ⚠️ **It still occupies its cell in the chunk array.** Skipping it
         * here hides it from the MESH, not from the world — breaking, reach and
         * the crosshair all go on seeing it.
         */
        if (drawnOutsideTheMesh(block)) continue;

        /**
         * 🥣 **The jata — T286 — drawn as boxes in THIS mesh.**
         *
         * 🔴 **The terrain's own material, never a new one** — a material made
         * while the game ran is what blacked out dev and staging once
         * (`06_Traps.md`). Its slabs wear the stone wall's tile, its board the
         * driftwood plank's and its hay the stubble's, so it needs no texture of
         * its own. **The texture is laid in metres**, so a 7 cm slab shows 7 cm
         * of stone, not a whole tile squashed.
         */
        if (isJata(block)) {
          const st = stateOf(block);
          const fill = typeof st['fill'] === 'number' ? st['fill'] : 0;
          const facing = (typeof st['facing'] === 'string' ? st['facing'] : 'north') as JataFacing;
          const blockJitter = jitterAt(origin.x + x, origin.y + y, origin.z + z);
          /* 🔦 The light ABOVE it — a solid cell's own light is dark by definition. */
          const lit = litAt(x, y + 1, z);
          for (const b of jataBoxes(fill, facing)) {
            const [tileBlock, tileFace] = JATA_TILE.get(b.stuff) ?? ['grjotgardur', 'side'];
            const id = blockIdForKey(tileBlock) ?? 0;
            for (const face of FACE_GEOMETRY) {
              const [dx, dy, dz] = face.dir;
              const layer = layerFor(id, tileFace === 'top' ? 1 : dy);
              const base = solid.positions.length / 3;
              for (const corner of face.corners) {
                const px = b.min[0] + corner.offset[0] * (b.max[0] - b.min[0]);
                const py = b.min[1] + corner.offset[1] * (b.max[1] - b.min[1]);
                const pz = b.min[2] + corner.offset[2] * (b.max[2] - b.min[2]);
                solid.positions.push(x + px, y + py, z + pz);
                solid.normals.push(dx, dy, dz);
                solid.ao.push(1);
                solid.jitter.push(blockJitter);
                solid.light.push(lit);
                /* The face loop's own convention, in metres: on a side, the
                   horizontal axis and v = 1 − height; on top and bottom, x and z. */
                solid.uvs.push(...(dy === 0 ? [dx === 0 ? px : pz, 1 - py] : [px, pz]));
                solid.layers.push(layer);
              }
              solid.indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
              solid.faces++;
            }
          }
          continue;
        }

        /**
         * 🌊 **Which bucket this block's faces go in — T158.**
         *
         * ⚠️ **Bound once per block, never per face.** A block is water or it
         * is ground; no face of one cube goes in a different bucket from its
         * neighbours, and asking per face would invite exactly that.
         *
         * The names below are the bucket's own arrays, so everything after this
         * line is the face loop it always was.
         */
        const buf = isFluid(block) ? fluid : solid;
        const { positions, normals, ao, jitter, light, uvs, layers, indices } = buf;

        /* ⚠️ Per block, not per face — all six faces of one cube shift together,
           or the cube stops reading as one object. Water is left alone: a sea
           where every square metre is a slightly different shade reads as
           static, and its life is meant to come from movement instead
           (Design §12.7).

           ⚠️ **Asks `isFluid`, not `isSolid`.** It used to ask `isSolid(block)`,
           which worked only because water was the one non-solid block in the
           palette. `kjarr` (T95) is the second, and it is scrub covering roughly
           a quarter of the world — under the old test every bush would have been
           byte-identical to every other, which is the exact flat-field fault T84
           existed to cure. `fluid` is now a flag in the palette rather than a
           block id hard-coded here. */
        const blockJitter = isFluid(block) ? 1 : jitterAt(origin.x + x, origin.y + y, origin.z + z);

        /* 1 for everything except a sub-metre block. See {@link hidesFace}. */
        const height = blockHeight(block);

        /**
         * 🌸 **A cross is not six faces, so it leaves the face loop alone.**
         *
         * 🔴 **Two planes at 45°, sharing one upward normal.** `Minecraft/13`
         * §1: each element carries `shade: false`, because **without it the two
         * planes take different directional shading and the thing reads as a
         * folded piece of card rather than as foliage.** Our equivalent is one
         * normal for all eight corners, and full brightness for the AO term.
         *
         * 🔴 **A small sideways shift, DERIVED FROM THE POSITION.** §3 calls
         * this *"the single cheapest thing in this document and the one most
         * likely to be skipped"* — without it every bush stands in a
         * ruler-straight row. ⚠️ **Derived, never random**, or the patch moves
         * every time the chunk is re-meshed — the rule `berries.ts` already
         * follows, and D2's reason: *a random answer cannot be tested.*
         *
         * ⚠️ **It costs an optimisation and that is known**: a cross occludes
         * nothing and merges with nothing. ✅ It is 4 triangles against a cube's
         * 12, so each one is cheaper; a meadow is the case to measure.
         */
        if (blockIsCross(block)) {
          const spread = CROSS_OFFSET;
          const ox = (hashPosition(origin.x + x, 7, origin.z + z) - 0.5) * 2 * spread;
          const oz = (hashPosition(origin.x + x, 13, origin.z + z) - 0.5) * 2 * spread;
          const cx = x + 0.5 + ox;
          const cz = z + 0.5 + oz;
          /* 🌸 As wide as it is tall, capped at the cell — see the constants. */
          const r = Math.min(CROSS_MAX_HALF_WIDTH, (height * CROSS_WIDTH_PER_HEIGHT) / 2);
          const lit = litAt(x, y, z);
          const layer = layerFor(block, 0);

          for (const [ax1, az1, ax2, az2] of CROSS_PLANES) {
            const base = positions.length / 3;
            /**
             * Bottom two, then top two — the winding the face loop uses.
             *
             * 🔴 **`u` runs ACROSS the plane and `v` runs DOWN it**, and the
             * first version of this derived one from the other: both bottom
             * corners got `u = 0`. **The texture was then stretched along the
             * plane's height with no horizontal variation at all**, and a patch
             * of scrub rendered as flat horizontal streaks lying on the ground.
             *
             * ⚠️ **Every test still passed** — face counts, normals, positions
             * and the offset were all correct, and none of them looks at what
             * the texture does. **It was found by baking a picture and looking
             * at it**, first try, which is the whole argument for doing that
             * before anything visual ships.
             *
             * `v = 1` at the bottom is the same convention the face loop uses:
             * the top of the block is the top of the tile.
             */
            const corner: readonly (readonly [number, number, number, number, number])[] = [
              [cx + ax1 * r, y, cz + az1 * r, 0, 1],
              [cx + ax2 * r, y, cz + az2 * r, 1, 1],
              [cx + ax2 * r, y + height, cz + az2 * r, 1, 0],
              [cx + ax1 * r, y + height, cz + az1 * r, 0, 0],
            ];
            for (const [px, py, pz, u, v] of corner) {
              positions.push(px, py, pz);
              /* 🔴 Straight up for BOTH planes — this is `shade: false`. */
              normals.push(0, 1, 0);
              ao.push(1);
              jitter.push(blockJitter);
              light.push(lit);
              uvs.push(u, v);
              layers.push(layer);
            }
            /* ⚠️ Both windings, so the plane is visible from either side —
               a bush seen from behind must not vanish. */
            indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
            indices.push(base, base + 2, base + 1, base, base + 3, base + 2);
            buf.faces++;
          }
          continue;
        }

        for (const face of FACE_GEOMETRY) {
          const [dx, dy, dz] = face.dir;

          /* The whole optimisation: a face is only drawn where something can
             actually see it. Everything buried is never emitted at all. */
          if (hidesFace(block, blockAt(x + dx, y + dy, z + dz), dy, height)) continue;

          /**
           * ⚠️ Which face this is decides which tile it draws.
           *
           * This is what makes a Minecraft grass block read as grass *on* dirt
           * rather than as a green cube: the living surface is on top and the
           * substrate is on the sides. For us `mosahraun` is the clearest case
           * — it literally *is* moss on lava, so its sides are basalt, and the
           * contrast is not a stylistic choice but what the ground is made of.
           *
           * Rock draws the same tile on all six faces, deliberately: sameness
           * is what separates it from anything growing.
           */
          /* 🌾 #813 — a field's side shows its grass band only where it is
             the TOP of the ground: nothing full and solid stands on it.
             🌿 #817 — and where that side is a one-metre step down onto MORE
             grass, the whole face is the field's own top: a grassy slope. */
          const layer =
            dy === 0 && !coveredBy(blockAt(x, y + 1, z))
              ? grassSlope(block, blockAt(x + dx, y, z + dz), blockAt(x + dx, y - 1, z + dz))
                ? layerFor(block, 1)
                : (edgeLayerFor(block) ?? layerFor(block, 0))
              : layerFor(block, dy);

          /* The empty cell the face looks out into. Every occlusion test is
             relative to this, not to the block itself. */
          const ax = x + dx;
          const ay = y + dy;
          const az = z + dz;

          const base = positions.length / 3;
          const shades: number[] = [];

          for (const corner of face.corners) {
            /* ⚠️ Only a FULL block occludes a corner. A 25 cm mat of kjarr
               shades nothing, and treating it as a wall would draw a dark ring
               around every bush — the classic mistake when sub-metre blocks
               arrive in an engine that assumed cubes.

               ⚠️ **Nor does a block you can see through (T144).** A crown is
               nine leaf blocks touching each other, so counting them as walls
               would put the darkest shade this engine has on every face inside
               it — and the whole point of drawing those faces is that light
               comes through them. It is the same rule as kjarr, for the same
               reason: occlusion is about what actually blocks light. */
            const solid = (o: readonly [number, number, number]): number => {
              const b = blockAt(ax + o[0], ay + o[1], az + o[2]);
              return blockHeight(b) === 1 && !isCutout(b) ? 1 : 0;
            };

            const s1 = solid(corner.side1);
            const s2 = solid(corner.side2);

            /* ⚠️ Two touching sides fully enclose the corner, and the diagonal
               behind them cannot be seen — so it is not consulted. Reading it
               anyway is the classic bug that makes an inside corner one shade
               too bright. */
            const open = s1 === 1 && s2 === 1 ? 0 : 3 - (s1 + s2 + solid(corner.diagonal));
            const shade = aoShade(open);
            shades.push(shade);

            /* ⚠️ The whole of "a block can be shorter than a metre" is this
               multiply. A corner's y offset is 0 or 1, so scaling it squashes
               the box down onto the floor of its own cell — which is where a
               deposit like scrub or snow actually sits. The tile stretches to
               fit rather than being cropped; that is invisible on noise, and
               would need thinking about for a slab with a pattern on it. */
            positions.push(
              x + corner.offset[0],
              y + corner.offset[1] * height,
              z + corner.offset[2],
            );
            normals.push(dx, dy, dz);
            /* ⚠️ Kept apart, not multiplied together. Both are "how much light
               this corner keeps", but one is shape and one is variety, and the
               shader fades them out at different rates with distance (T94).
               Multiplying here would throw away the only thing that tells them
               apart. */
            ao.push(shade);
            jitter.push(blockJitter);
            /* 🔦 **The light in the cell this face LOOKS OUT INTO**, never the
               light inside the block itself — a solid block's own cell is dark
               by definition, so reading it would leave every surface unlit no
               matter how many torches were burning in front of it. */
            light.push(litAt(ax, ay, az));
            uvs.push(corner.uv[0], corner.uv[1]);
            layers.push(layer);
          }

          /**
           * ⚠️ Split the quad along its **brighter** diagonal.
           *
           * A quad is two triangles, and the shading is interpolated across
           * each one independently. Pick the wrong diagonal and a single dark
           * corner bleeds along two edges instead of staying in its own
           * triangle — the well-known voxel AO artifact, where flat ground
           * grows a herringbone.
           *
           * Choosing the diagonal whose ends are least occluded keeps the dark
           * corner confined to one triangle, which is what the eye expects.
           */
          const q0 = shades[0] ?? 1;
          const q1 = shades[1] ?? 1;
          const q2 = shades[2] ?? 1;
          const q3 = shades[3] ?? 1;

          if (q0 + q2 >= q1 + q3) {
            indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
          } else {
            indices.push(base + 1, base + 2, base + 3, base + 1, base + 3, base);
          }
          buf.faces++;
        }
      }
    }
  }

  return { ...pack(solid), water: pack(fluid), litAt };
}

/** One bucket's plain arrays, packed into the typed arrays the GPU wants. */
function pack(buf: Buf): ChunkGeometry {
  return {
    positions: new Float32Array(buf.positions),
    normals: new Float32Array(buf.normals),
    ao: new Float32Array(buf.ao),
    jitter: new Float32Array(buf.jitter),
    light: new Float32Array(buf.light),
    uvs: new Float32Array(buf.uvs),
    layers: new Float32Array(buf.layers),
    indices: new Uint32Array(buf.indices),
    faceCount: buf.faces,
  };
}
